import React, { useState, useEffect } from 'react';
import { 
  Typography, Container, Paper, Box, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Avatar, Button, Chip,
  Tooltip, TextField
} from '@mui/material';
import { useSelector } from 'react-redux';
import { getAllAttendance, updateAttendanceStatus } from '../../api/attendanceAPI';
import { getAllUsers } from '../../api/userAPI';
import moment from 'moment';

const Teams = () => {
  const { user } = useSelector((state) => state.tokens);
  
  const [users, setUsers] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [previousHalfDays, setPreviousHalfDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));

  const fetchData = async () => {
    setLoading(true);
    try {
      const [uRes, aRes, allRes] = await Promise.all([
        getAllUsers(),
        getAllAttendance({ date: selectedDate }),
        getAllAttendance({ range: 'yearly' })
      ]);

      if(uRes.data) {
        const activeOfficeEmp = uRes.data.filter(u => u.isOfficeEmp && u.isActive !== false);
        setUsers(activeOfficeEmp);

        const activeIds = activeOfficeEmp.map(u => u.id || u._id);
        if(aRes.data && aRes.data.success) {
          setAttendance(aRes.data.log.filter(a => activeIds.includes(a.user?._id)));
        }
        
        if(allRes.data && allRes.data.success) {
          const todayStr = moment().format('YYYY-MM-DD');
          const halfDays = allRes.data.log.filter(a => 
            activeIds.includes(a.user?._id) && 
            a.status === 'Half-day' && 
            a.date !== todayStr &&
            new Date(a.date) >= new Date('2026-06-01')
          );
          // Sort by date descending
          halfDays.sort((a, b) => new Date(b.date) - new Date(a.date));
          setPreviousHalfDays(halfDays);
        }
      } else {
        if(aRes.data && aRes.data.success) setAttendance(aRes.data.log);
      }
    } catch(err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  const handleUpdateAttendance = async (id, status, halfDayStatus) => {
    try {
      await updateAttendanceStatus(id, { status, halfDayStatus });
      fetchData();
    } catch (err) { console.error(err); }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 4 }}>Teams Management</Typography>
      
      <Paper sx={{ borderRadius: 3, overflow: 'hidden', p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">Team Overview</Typography>
          <TextField
            type="date"
            size="small"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            sx={{ width: 180 }}
          />
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><b>Employee</b></TableCell>
                <TableCell><b>Today's Status</b></TableCell>
                <TableCell><b>Check In</b></TableCell>
                <TableCell><b>Check Out</b></TableCell>
                <TableCell><b>Actions</b></TableCell>
                <TableCell><b>Email</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((emp) => {
                const log = attendance.find(a => a.user?._id === (emp.id || emp._id));
                
                const empIdStr = (emp.id || emp._id).toString();
                let joinDate = moment('2000-01-01');
                if (empIdStr.length === 24) {
                  joinDate = moment(new Date(parseInt(empIdStr.substring(0, 8), 16) * 1000)).startOf('day');
                }
                const isBeforeJoin = moment(selectedDate).isBefore(joinDate, 'day');

                let statusLabel = log?.status === 'Half-day' ? `Half-day (${log.halfDayStatus})` : (log?.status || 'Absent');
                let statusColor = log?.status === 'Present' ? 'success' : (log?.status === 'Half-day' ? 'warning' : 'error');

                if (!log && isBeforeJoin) {
                  statusLabel = 'Not Joined';
                  statusColor = 'default';
                }

                return (
                  <TableRow key={emp.id || emp._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: '#eee', color: '#666', width: 32, height: 32, fontSize: '0.9rem' }}>
                          {emp.fullName?.[0]}
                        </Avatar>
                        <Typography variant="body2" fontWeight="bold">{emp.fullName}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Chip 
                          label={statusLabel} 
                          color={statusColor} 
                          size="small" 
                        />
                        {log?.halfDayStatus === 'Pending' && log?.earlyCheckoutReason && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Reason: {log.earlyCheckoutReason}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{log?.checkIn ? moment(log.checkIn).format('hh:mm A') : '-'}</TableCell>
                    <TableCell>{log?.checkOut ? moment(log.checkOut).format('hh:mm A') : '-'}</TableCell>
                    <TableCell>
                      {log && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {log.status === 'Half-day' ? (
                            <Tooltip title="Convert to Full Day (Release Penalty)">
                              <Button 
                                size="small" 
                                variant="outlined" 
                                color="success"
                                onClick={() => handleUpdateAttendance(log._id, 'Present', 'Rejected')}
                              >
                                Release (Full Day)
                              </Button>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Manually Mark as Half-day">
                              <Button 
                                size="small" 
                                variant="outlined" 
                                color="warning"
                                onClick={() => handleUpdateAttendance(log._id, 'Half-day', 'Approved')}
                              >
                                Mark Half-day
                              </Button>
                            </Tooltip>
                          )}

                          {log.halfDayStatus === 'Pending' && (
                            <Tooltip title="Approve as Half-day">
                              <Button 
                                size="small" 
                                variant="contained" 
                                color="warning" 
                                onClick={() => handleUpdateAttendance(log._id, 'Half-day', 'Approved')}
                              >
                                Approve 1/2
                              </Button>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell><Typography variant="caption">{emp.email}</Typography></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ borderRadius: 3, overflow: 'hidden', p: 3, mt: 4 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Previous Half-Days Log</Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><b>Date</b></TableCell>
                <TableCell><b>Employee</b></TableCell>
                <TableCell><b>Status</b></TableCell>
                <TableCell><b>Reason</b></TableCell>
                <TableCell><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {previousHalfDays.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center">No previous half-days found.</TableCell></TableRow>
              ) : previousHalfDays.map((log) => (
                <TableRow key={log._id} hover>
                  <TableCell>{moment(log.date).format('DD MMM YYYY')}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ bgcolor: '#eee', color: '#666', width: 32, height: 32, fontSize: '0.9rem' }}>
                        {log.user?.fullName?.[0]}
                      </Avatar>
                      <Typography variant="body2" fontWeight="bold">{log.user?.fullName}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={`Half-day (${log.halfDayStatus})`} 
                      color="warning" 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>
                    {log.earlyCheckoutReason ? (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        {log.earlyCheckoutReason}
                      </Typography>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Convert to Full Day (Release Penalty)">
                        <Button 
                          size="small" 
                          variant="outlined" 
                          color="success"
                          onClick={() => handleUpdateAttendance(log._id, 'Present', 'Rejected')}
                        >
                          Release (Full Day)
                        </Button>
                      </Tooltip>
                      {log.halfDayStatus === 'Pending' && (
                        <Tooltip title="Approve as Half-day">
                          <Button 
                            size="small" 
                            variant="contained" 
                            color="warning" 
                            onClick={() => handleUpdateAttendance(log._id, 'Half-day', 'Approved')}
                          >
                            Approve 1/2
                          </Button>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default Teams;
